import { EChart } from './EChart';
import type { EChartsOption } from 'echarts';
import { useMemo } from 'react';
import { tokens } from '@xdface/tokens';
import { useTheme } from '../../contexts/ThemeContext';
import { resolveCssVar } from '../../utils/css-vars';

interface DayActivity {
  date: string;
  count: number;
}

interface ActivityHeatmapProps {
  data: DayActivity[];
  weeks?: number;
}

export function ActivityHeatmap({ data }: ActivityHeatmapProps) {
  const { theme } = useTheme();

  const option = useMemo<EChartsOption>(() => {
    const palette = tokens.theme[theme];
    const end = new Date();
    const start = new Date();
    start.setMonth(start.getMonth() - 11);
    start.setDate(1);

    const realCounts = new Map(data.map((d) => [d.date, d.count]));
    // visualMap saturates at 10 so a busy day stays tangerine instead of
    // falling through outOfRange into the empty-cell color. The tooltip
    // still reports the real total.
    const seriesData = data.map((d) => [d.date, d.count <= 0 ? 0 : Math.min(d.count, 10)]);

    // echarts renders SVG/canvas and cannot resolve var(), so theme tokens
    // are read from the document root (fallbacks = @xdface/tokens values).
    // Days with no series point use the calendar color. Count 0 is out of
    // the visual range and uses the same empty color. Active days run from
    // the active theme's action to its secondary accent, including neutral Mono.
    const readToken = (name: string, fallback: string) => resolveCssVar(name, fallback, theme);
    // Color paper keeps every surface white; neutral cell ink must remain visible.
    const emptyCell =
      theme === 'light'
        ? readToken('--xd-bd', palette.bd)
        : readToken('--xd-raised', palette.raised);
    const leaf = readToken('--xd-action', palette.action);
    const tangerine = readToken('--xd-action-end', palette.actionEnd);
    const cellBorder = readToken('--xd-bg', palette.bg);
    const labelColor = readToken('--xd-fg-3', palette.fg3);

    return {
      backgroundColor: 'transparent',
      tooltip: {
        trigger: 'item' as const,
        formatter: (params: unknown) => {
          const { data: point } = params as { data: [string, number] };
          const [date, visualCount] = point;
          const count = realCounts.get(date) ?? visualCount;
          const d = new Date(date);
          const label = d.toLocaleDateString('es-AR', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          });
          return `<div style="font-size:12px"><strong>${label}</strong><br/>${count} actividad${count !== 1 ? 'es' : ''}</div>`;
        },
      },
      visualMap: {
        min: 1,
        max: 10,
        calculable: false,
        show: false,
        inRange: {
          color: [leaf, tangerine],
        },
        outOfRange: {
          color: emptyCell,
        },
      },
      calendar: {
        top: 20,
        left: 40,
        right: 10,
        bottom: 10,
        range: [start.toISOString().split('T')[0], end.toISOString().split('T')[0]],
        cellSize: [14, 14],
        splitLine: { show: false },
        itemStyle: {
          color: emptyCell,
          borderWidth: 2,
          borderColor: cellBorder,
          borderRadius: 3,
        },
        yearLabel: { show: false },
        monthLabel: {
          nameMap: [
            'Ene',
            'Feb',
            'Mar',
            'Abr',
            'May',
            'Jun',
            'Jul',
            'Ago',
            'Sep',
            'Oct',
            'Nov',
            'Dic',
          ],
          color: labelColor,
          fontSize: 11,
        },
        dayLabel: {
          firstDay: 1,
          nameMap: ['D', 'L', 'M', 'X', 'J', 'V', 'S'],
          color: labelColor,
          fontSize: 10,
        },
      },
      series: [
        {
          type: 'heatmap',
          coordinateSystem: 'calendar',
          data: seriesData,
          emphasis: {
            itemStyle: {
              shadowBlur: 8,
              shadowColor: leaf,
            },
          },
        },
      ],
    };
  }, [data, theme]);

  return <EChart option={option} style={{ height: '160px', width: '100%' }} renderer="svg" />;
}
